import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import {
  taxonomiesQueryOptions,
  useBulkDeleteTaxonomies,
  type TaxonomySummary,
} from '@/entities/taxonomy';
import { usePermissions } from '@/entities/user';
import { PageTransition } from '@/shared/ui/animations';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';
import { ROUTES } from '@/shared/config/routes';
import { CreateTaxonomyModal } from './components/CreateTaxonomyModal/CreateTaxonomyModal';
import { TaxonomyTable } from './components/TaxonomyTable/TaxonomyTable';
import { TaxonomyToolbar } from './components/TaxonomyToolbar/TaxonomyToolbar';
import { TaxonomyReasonModal } from './components/TaxonomyReasonModal/TaxonomyReasonModal';
import {
  TaxonomyErrorState,
  TaxonomyNoResultsState,
  TaxonomySchemaEmptyState,
} from '../components/TaxonomyStates/TaxonomyStates';
import { type TaxonomyStatusFilter } from './constants/taxonomy.constants';

function matchesSearch(taxonomy: TaxonomySummary, term: string): boolean {
  if (!term) return true;
  return taxonomy.name.toLowerCase().includes(term);
}

function matchesStatus(taxonomy: TaxonomySummary, filter: TaxonomyStatusFilter): boolean {
  if (filter === 'all') return true;
  return taxonomy.status === filter;
}

/** Schemas tab. Chrome (header, tabs, content frame) lives in `TaxonomyLayout`. */
export function TaxonomyList() {
  const { t } = useTranslation('knowledge');
  const navigate = useNavigate();
  // Every mutating endpoint behind this tab requires the manage permission, so
  // without it the write affordances are absent rather than 403-on-click.
  const { canManageTaxonomy } = usePermissions();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<TaxonomyStatusFilter>('all');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

  const query = useQuery(taxonomiesQueryOptions());

  const taxonomies = useMemo<TaxonomySummary[]>(() => query.data ?? [], [query.data]);

  const filtered = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    return taxonomies.filter((t) => matchesStatus(t, statusFilter) && matchesSearch(t, term));
  }, [taxonomies, debouncedSearch, statusFilter]);

  const handleCreateClick = useCallback(() => setCreateModalOpen(true), []);
  const handleModalClose = useCallback(() => setCreateModalOpen(false), []);

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setStatusFilter('all');
  }, []);

  const { refetch } = query;
  const handleRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const filterActive = debouncedSearch.trim().length > 0 || statusFilter !== 'all';
  const showError = query.isError && taxonomies.length === 0;
  const showInitialEmpty = !query.isLoading && !query.isError && taxonomies.length === 0;
  const showNoResults =
    !query.isLoading &&
    !query.isError &&
    taxonomies.length > 0 &&
    filtered.length === 0 &&
    filterActive;

  // Hide the toolbar in the true-empty + error states (mirrors the other
  // Knowledge Hub lists) — with no schemas there's nothing to search/filter.
  const showToolbar = !showInitialEmpty && !showError;

  // Selection is scoped to VISIBLE rows so a filter can't hide something a
  // "select all" then deletes.
  const visibleSelected = useMemo(
    () => filtered.filter((t) => selectedIds.has(t.id)),
    [filtered, selectedIds],
  );
  const allVisibleSelected = filtered.length > 0 && visibleSelected.length === filtered.length;

  // Functional updater: batched ticks would otherwise each build from the same
  // stale snapshot and drop all but the last.
  const toggleRow = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllVisible = () =>
    setSelectedIds(allVisibleSelected ? new Set() : new Set(filtered.map((t) => t.id)));

  const bulkDelete = useBulkDeleteTaxonomies({
    onSuccess: () => {
      setBulkDeleteOpen(false);
      setSelectedIds(new Set());
    },
  });

  return (
    <>
      <PageTransition variant="fade" className="flex min-h-0 flex-1 flex-col">
        {showToolbar && (
          <TaxonomyToolbar
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            search={search}
            onSearchChange={setSearch}
            onCreateClick={canManageTaxonomy ? handleCreateClick : undefined}
            selectedCount={visibleSelected.length}
            onBulkDelete={canManageTaxonomy ? () => setBulkDeleteOpen(true) : undefined}
          />
        )}
        {showError ? (
          <TaxonomyErrorState onRetry={handleRetry} />
        ) : showInitialEmpty ? (
          <TaxonomySchemaEmptyState onCreate={canManageTaxonomy ? handleCreateClick : undefined} />
        ) : showNoResults ? (
          <TaxonomyNoResultsState
            title={t('taxonomy.taxonomyStates.noSchemasFound')}
            description={t('taxonomy.taxonomyStates.noSchemasMatchYourCurrent')}
            onClear={handleClearFilters}
          />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col overflow-auto px-section pb-section">
            {/* Selection exists only to bulk-delete, so it goes away with it
                rather than offering checkboxes that lead nowhere. */}
            <TaxonomyTable
              taxonomies={filtered}
              isLoading={query.isLoading}
              selectedIds={selectedIds}
              onToggleRow={canManageTaxonomy ? toggleRow : undefined}
              onToggleAll={canManageTaxonomy ? toggleAllVisible : undefined}
              canManage={canManageTaxonomy}
            />
          </div>
        )}
      </PageTransition>

      {/* Deleting a schema is a lifecycle change, so it carries the same
          mandatory justification a single delete does — one reason for the batch. */}
      <TaxonomyReasonModal
        open={bulkDeleteOpen}
        title={t('taxonomy.taxonomyList.deleteSchemasTitle', { count: visibleSelected.length })}
        description={t('taxonomy.taxonomyList.everyVersionHistoryGoesThem')}
        confirmLabel={t('taxonomy.taxonomyList.deleteSchemas', { count: visibleSelected.length })}
        isSubmitting={bulkDelete.isPending}
        onCancel={() => setBulkDeleteOpen(false)}
        onConfirm={(reason) => bulkDelete.mutate({ ids: visibleSelected.map((t) => t.id), reason })}
      />

      {/* Unmount on close so the modal's internal form state resets. */}
      {createModalOpen && (
        <CreateTaxonomyModal
          open
          onClose={handleModalClose}
          // The modal already forwards this; without a handler, creating a
          // schema closed the dialog and left the user on the list, with the
          // new empty schema indistinguishable from the rest of the table.
          onCreated={(created) => void navigate(ROUTES.KNOWLEDGE_HUB_TAXONOMY_DETAIL(created.id))}
        />
      )}
    </>
  );
}

TaxonomyList.displayName = 'TaxonomyList';
